import React, { useRef } from 'react';

import { Spinner } from '@/components/Spinner';
import { useInfiniteScroll } from '@/hooks';

import * as S from './listing.styles';
import { ListingProps } from './listing.types';

type Must<T> = T extends object ? T : never;
type DefaultWrapperOptionType = Must<
  ListingProps<{ id: number | string }>['defaultWrapperOptions']
>;

const ListWrapper: React.FC<{
  children: React.ReactNode;
  customWrapper: ListingProps<{ id: number | string }>['customWrapper'];
  defaultWrapperOptions: DefaultWrapperOptionType;
}> = ({ customWrapper: CustomWrapper, defaultWrapperOptions, children }) => {
  if (CustomWrapper) {
    return <CustomWrapper>{children}</CustomWrapper>;
  }

  return (
    <S.DefaultListWrapper
      $gapSize={defaultWrapperOptions.gapSize}
      $itemWidth={defaultWrapperOptions.itemWidth}
    >
      {children}
    </S.DefaultListWrapper>
  );
};

export const Listing = <T extends { id: string }>({
  itemComponent: ItemComponent,
  notFoundComponent: NotFoundComponent,
  customWrapper: CustomWrapper,
  defaultWrapperOptions = { gapSize: '40px', itemWidth: '200px' },
  selection = [],
  fetchMore,
}: ListingProps<T>) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const { items, pending } = useInfiniteScroll<T>(wrapperRef, fetchMore);

  return (
    <S.Wrapper ref={wrapperRef}>
      {items.length === 0 && NotFoundComponent && !pending ? (
        <NotFoundComponent />
      ) : (
        <>
          <ListWrapper customWrapper={CustomWrapper} defaultWrapperOptions={defaultWrapperOptions}>
            {items.map((item) => {
              const selected = selection.includes(item.id);
              return <ItemComponent key={item.id} selected={selected} {...item} />;
            })}
          </ListWrapper>
          <S.StatusInfoWrapper>{pending && <Spinner type="circle" />}</S.StatusInfoWrapper>
        </>
      )}
    </S.Wrapper>
  );
};
